import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TbWorld } from 'react-icons/tb';
import { IoLocationSharp } from 'react-icons/io5';
import { PiToolboxFill } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { CiInstagram } from 'react-icons/ci';
import { FaLinkedinIn, FaSquareXTwitter, FaWhatsapp } from 'react-icons/fa6';
import { IoLogoFacebook } from 'react-icons/io';
import { BeatLoader } from 'react-spinners';
import Pagination from '../Pagination';
import { setCurrentPage } from '../../store/slice/paginationSlice';
import { useBusinessData } from '../../hooks/BusinessManagement/useBusinessData';
import {
  fetchBusinessApplications,
  processBusinessApplications,
} from '../../services/BusinessManagement/BusinessRequestService';
import { formatYear } from '../../utils/Date';
import getImage from '../../assets/Assets';
import Title from '../Title';

const BUSINESS_REQUEST_ENTRIES = 8;

const BusinessRequest = () => {
  const {
    data: businessList = [],
    loading: isLoading,
    error,
    refetchData,
  } = useBusinessData(fetchBusinessApplications, processBusinessApplications);

  const [displayedBusiness, setDisplayedBusiness] = useState([]);
  const [navigatingId, setNavigatingId] = useState(null);
  const Calendar = getImage('calender2');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchQuery = useSelector(state => state.search.query);
  const { currentPage } = useSelector(state => state.pagination);
  const entriesPerPage = BUSINESS_REQUEST_ENTRIES;

  const socialIcons = useMemo(
    () => ({
      linkedin: <FaLinkedinIn />,
      facebook: <IoLogoFacebook />,
      whatsapp: <FaWhatsapp />,
      twitter: <FaSquareXTwitter />,
      instagram: <CiInstagram />,
    }),
    []
  );

  const filteredResults = useMemo(() => {
    if (!businessList || !Array.isArray(businessList)) return [];

    if (!searchQuery) return businessList;

    return businessList.filter(business => {
      const searchFields = [
        business?.firstName,
        business?.lastName,
        business?.email,
        business?.company,
        business?.name,
      ];

      return searchFields.some(
        field => field && field.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [searchQuery, businessList]);

  useEffect(() => {
    setDisplayedBusiness(filteredResults);
    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  }, [filteredResults, dispatch]);

  const paginatedBusiness = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return displayedBusiness.slice(startIndex, endIndex);
  }, [displayedBusiness, currentPage, entriesPerPage]);

  const handleView = useCallback(
    async businessId => {
      if (!businessId) return;

      setNavigatingId(businessId);
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        navigate(`/business/list/${businessId}`);
      } finally {
        setNavigatingId(null);
      }
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex flex-col items-center justify-center">
        <div className="relative px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded error-message">
          {error}
          <button
            onClick={refetchData}
            className="flex items-center mt-2 text-sm text-black hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 min-h-screen w-[80%] bg-[#F4F5F6] font-nohemi flex flex-col">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-6">
          <div className="text-nowrap">
            <Title title="Business Listing" subtitle="Business Listings &gt; Requests" />
          </div>
        </div>

        {paginatedBusiness.length === 0 ? (
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {searchQuery ? 'No matching businesses found' : 'No businesses available'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 px-4 mt-4 mb-6 md:grid-cols-2 lg:grid-cols-4">
            {paginatedBusiness.map(business => {
              const socialLinks = (business?.sociallink || []).slice(0, 2);

              return (
                <div
                  key={business._id}
                  data-testid="business-card"
                  className="h-full w-[260px] px-6 py-4 bg-white rounded-2xl">
                  <div className="flex items-start my-2 space-x-2">
                    <img
                      className="w-[60px] h-[60px] rounded-lg object-cover flex-shrink-0"
                      src={
                        business?.profilePic ||
                        business?.media?.[0]?.fileUrl ||
                        '/default-business.png'
                      }
                      alt={`${business.company || business.name || 'Business'} logo`}
                    />
                    <div className="flex-grow">
                      <p className="text-base font-semibold text-[#000000] text-wrap">
                        {business.name || business.company || 'Unnamed Business'}
                      </p>
                      <p className="text-sm text-[#626262] inline-flex items-center">
                        <img src={Calendar} alt="calendar" className="w-3 h-3 mr-1" />
                        Est.{' '}
                        {formatYear(business?.founded_year || business?.yearEstablished) || 'N/A'}
                      </p>
                      <div>
                        <button
                          className={`font-medium text-sm px-2 py-1 rounded-sm ${
                            business.applicationStatus === 'Pending'
                              ? 'text-red-500 bg-red-100'
                              : 'text-[#353DFF] bg-[#BCCFFF33]'
                          }`}>
                          {business.applicationStatus || '-'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-4">
                    <div>
                      <p className="text-base font-semibold text-[#000000]">Short Bio</p>
                      <p className="text-sm font-light text-[#404D61] line-clamp-3 mt-1.5">
                        {business.bio || 'No bio available'}
                      </p>
                    </div>

                    <div>
                      <p className="text-base font-semibold mb-2  text-[#000000]">Basic Info</p>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <PiToolboxFill className="text-xs" />
                          <p className="text-sm mx-2 font-light text-[#404D61]">
                            {business?.serviceType?.name || 'No service available'}
                          </p>
                        </div>
                        <div className="flex items-center">
                          <IoLocationSharp className="text-xs" />
                          <p className="text-sm mx-2 font-light text-[#686575]">
                            {business?.location?.address ||
                              business?.locations ||
                              business?.location ||
                              'Location not specified'}
                          </p>
                        </div>
                        <div className="flex items-center">
                          <TbWorld className="text-xs" />
                          <p className="text-sm mx-2 font-light text-[#686575]">
                            {business.website || 'Website not provided'}
                          </p>
                        </div>
                        {socialLinks.length > 0 && (
                          <div className="flex space-x-2">
                            {socialLinks.map((social, index) => {
                              const platform = social.platform?.toLowerCase();
                              const Icon = socialIcons[platform];

                              return Icon ? (
                                <a
                                  key={social._id || `social-${index}`}
                                  href={social.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm flex items-center"
                                  aria-label={platform}>
                                  {React.cloneElement(Icon, { className: 'w-5 h-5 mx-0.5' })}
                                </a>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <button
                        onClick={() => handleView(business._id)}
                        disabled={navigatingId === business._id}
                        className="border bg-black text-white text-sm px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                        aria-label={`View ${business.company || business.name || 'Business'}`}>
                        {navigatingId === business._id ? 'Loading...' : 'View Business'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {displayedBusiness.length > entriesPerPage && (
        <div className="pt-4 mt-auto">
          <Pagination totalEntries={displayedBusiness.length} />
        </div>
      )}
    </div>
  );
};

export default BusinessRequest;
